/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useCallback } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Toaster } from "../components/ui/sonner";
import { useAuth } from "@/hooks/auth";
import { UserWithRole } from "@/hooks/auth/types";
import { useLanguage } from "@/hooks/useLanguage";
import { motion, AnimatePresence } from "framer-motion";
import { useBackground } from "@/contexts/BackgroundContext";
import Footer from "@/components/Footer";
import MacOSNavbar from "@/components/ui/MacOSNavbar";
import AuthRedirectDebug from "@/components/AuthRedirectDebug";
import usePageTitle from "@/hooks/usePageTitle";
import { AlertProvider } from "@/components/ui/MacOSAlert";

const GlobalLayout = () => {
  const { user } = useAuth();
  const { currentLanguage, switchLanguage, t } = useLanguage();
  const { currentBg } = useBackground();
  const location = useLocation();

  // Set a default page title based on the current route
  const getDefaultPageTitle = () => {
    const path = location.pathname;

    if (path === "/" || path === "/user/dashboard") return "dashboard";
    if (path.includes("/courses")) return "allCourses";
    if (path.includes("/admin")) return "adminPanel";
    if (path.includes("/user/profile")) return "profile";
    if (path.includes("/user/certificates")) return "certificates";
    if (path.includes("/amazing")) return "amazing";

    // Default title if no specific path is matched
    return "learningPlatform";
  };

  // Use the default page title
  usePageTitle(getDefaultPageTitle());

  // Handle user as UserWithRole
  const userWithRole = user as UserWithRole;

  // Get current background image based on the selected color
  const getBackgroundImagePath = useCallback(() => {
    const bgMapping = {
      blue: "/images/blueBG.jpg",
      green: "/images/greenBG.jpg",
      purple: "/images/purpleBG.jpg",
      red: "/images/redPageBg.jpg",
      orange: "/images/orangeBG.jpg",
      yellow: "/images/yellowBG.jpg",
    };

    return (
      bgMapping[currentBg as keyof typeof bgMapping] || "/images/blueBG.jpg"
    );
  }, [currentBg]);

  // Setup background image effect
  useEffect(() => {
    // Create a new stylesheet for global styles
    const styleSheet = document.createElement("style");
    styleSheet.id = "global-layout-styles";

    styleSheet.textContent = `
      body {
        margin: 0;
        padding: 0;
        overflow-x: hidden;
        background-color: transparent !important; /* Make sure it's transparent */
        background: transparent !important;
        color: white;
      }
      .widget-glass {
        background: rgba(20, 20, 40, 0.7) !important; /* Subtle blue-black tint at 70% opacity */
        backdrop-filter: blur(30px) !important; /* Increased blur for that macOS feel */
        -webkit-backdrop-filter: blur(30px) !important;
        border: 1px solid rgba(255, 255, 255, 0.15) !important; /* Slightly more subtle border */
        border-radius: 1.25rem !important; /* Slightly rounder corners like macOS */
        max-width: 100% !important;
        overflow: hidden !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2) !important; /* Softer shadow */
      }
      /* Widget header specifically matching the reference image */
      .widget-glass > div:first-child {
        border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
        background: rgba(0, 0, 0, 0.1) !important; /* Slightly darker header */
      }
      /* Control buttons matching macOS style */
      .widget-glass .rounded-full.bg-red-500,
      .widget-glass .rounded-full.bg-yellow-500,
      .widget-glass .rounded-full.bg-green-500 {
        opacity: 0.8;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      }
      .global-bg {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: -1;
        overflow: hidden;
      }
      
      .global-bg-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        /* Increased brightness to make background clearer */
        filter: brightness(0.95);
        transition: all 0.5s ease-in-out;
      }

      .app-glass-card {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 1rem;
        overflow: hidden;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
      }

      .app-header {
        background: rgba(255, 255, 255, 0.08);
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }
      
      .text-shadow {
        text-shadow: 0 2px 4px rgba(0,0,0,0.5);
      }
      
      /* Add color-specific custom styles based on current background */
      body[data-bg-color="${currentBg}"] .app-glass-card {
        /* Adjust transparency based on background color */
        background: rgba(255, 255, 255, ${
          currentBg === "blue" || currentBg === "purple" ? "0.08" : "0.1"
        });
      }
    `;

    document.head.appendChild(styleSheet);

    // Create a background container if it doesn't exist
    let bgContainer = document.getElementById("global-bg");
    if (!bgContainer) {
      bgContainer = document.createElement("div");
      bgContainer.id = "global-bg";
      bgContainer.className = "global-bg";
      document.body.appendChild(bgContainer);
    }

    // Create or update background image
    let bgImage = document.querySelector(
      ".global-bg-image"
    ) as HTMLImageElement;
    if (!bgImage) {
      bgImage = document.createElement("img");
      bgImage.className = "global-bg-image";
      bgContainer.appendChild(bgImage);
    }

    // Set background image source with the path based on current bg
    const bgPath = getBackgroundImagePath();
    bgImage.src = bgPath;

    // Cleanup function
    return () => {
      const styleEl = document.getElementById("global-layout-styles");
      if (styleEl) {
        document.head.removeChild(styleEl);
      }

      const bgEl = document.getElementById("global-bg");
      if (bgEl) {
        document.body.removeChild(bgEl);
      }
    };
  }, [currentBg, getBackgroundImagePath]); // Re-run when background color changes

  // Determine if we should use the GlobalLayout or custom course layout
  const isCourseLesson = location.pathname.includes("/lesson/");

  // For course lesson pages, we'll use a minimal layout without the default styling
  if (isCourseLesson) {
    return (
      <AlertProvider>
        <div className="min-h-screen">
          {/* Course specific pages don't get the global styling */}
          <main className="w-full">
            <Outlet />
          </main>

          {/* Toast notifications */}
          <Toaster />

          {/* Auth Debug Tool - Remove in production */}
          {process.env.NODE_ENV !== "production" && <AuthRedirectDebug />}
        </div>
      </AlertProvider>
    );
  }

  // Default global layout for non-course pages
  return (
    <AlertProvider>
      <div className="min-h-screen overflow-hidden text-white">
        {/* Apple-style Global Navbar */}
        <MacOSNavbar />

        {/* Main Content */}
        <main className="flex-1 w-full overflow-x-hidden">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-16 pb-16 overflow-hidden">
            <Outlet />
          </div>
        </main>

        {/* Apple-style Footer - kept at full width */}
        <Footer />

        {/* Toast notifications */}
        <Toaster />

        {/* Auth Debug Tool - Remove in production */}
        {process.env.NODE_ENV !== "production" && <AuthRedirectDebug />}
      </div>
    </AlertProvider>
  );
};

export default GlobalLayout;
